import * as React from "react";
import { cn } from "@/lib/utils";

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentId: string;
}
const PopoverContext = React.createContext<PopoverContextValue | null>(null);
function usePopover() {
  const context = React.useContext(PopoverContext);
  if (!context) throw new Error("Popover parts must be inside Popover");
  return context;
}

function Popover({
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const contentId = React.useId();
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  return (
    <PopoverContext.Provider value={{ open, setOpen, triggerRef, contentId }}>
      {children}
    </PopoverContext.Provider>
  );
}

function PopoverTrigger({ className, onClick, ...props }: React.ComponentProps<"button">) {
  const { open, setOpen, triggerRef, contentId } = usePopover();
  return (
    <button
      ref={triggerRef}
      type="button"
      aria-expanded={open}
      aria-controls={open ? contentId : undefined}
      className={cn(
        "inline-flex items-center justify-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) setOpen(!open);
      }}
      {...props}
    />
  );
}

function PopoverContent({ className, onKeyDown, ...props }: React.ComponentProps<"div">) {
  const { open, setOpen, triggerRef, contentId } = usePopover();
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (
        !ref.current?.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen, triggerRef]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      id={contentId}
      role="dialog"
      className={cn(
        "absolute z-50 w-72 rounded-lg border bg-popover p-4 text-popover-foreground shadow-dialog outline-none",
        className,
      )}
      onKeyDown={onKeyDown}
      {...props}
    />
  );
}

export { Popover, PopoverContent, PopoverTrigger };
